import { AIRPORTS } from './airports.js';
import { CONFIG, OBSTACLES, GATES, GATE_WALLS, BOUNDARIES, DIVIDER } from './config.js';
import { AirportNav } from './navigation.js';
import { expansionsOpen, expansionOpensWith, standingHoarding } from '../content/placement.js';

/** One shared floor plan; an airport may bring its own gate row (Summit Ridge), which changes only gate geometry. */
export const DEFAULT_LAYOUT = 'terminal-v1' as const;
export type LayoutId = typeof DEFAULT_LAYOUT;
/** Navigation grids per floor plan and gate row ("terminal-v1" or "terminal-v1:SMR"). */
const navs = new Map<string, AirportNav>();
export type GateLayout = NonNullable<(typeof AIRPORTS)[number]['gateLayout']>;
export type Expansion = NonNullable<GateLayout['EXPANSIONS']>[number];
type Rect = GateLayout['OBSTACLES'][number];
const SHARED_GATES: GateLayout = { GATES, GATE_WALLS, BOUNDARIES, OBSTACLES, DIVIDER };

/** The floor plan an authored airport uses (recorded in terminal records); null for an unknown airport. */
export function layoutIdFor(airportId: string): LayoutId | null {
    return AIRPORTS.some(p => p.code === airportId) ? DEFAULT_LAYOUT : null;
}
/** The plan plus gate row an airport is built with: equal keys mean identical walls, gates and navigation. */
export function gatePlanKey(airportId: string): string | null {
    const port = AIRPORTS.find(p => p.code === airportId);
    return !port ? null : port.gateLayout ? `${DEFAULT_LAYOUT}:${port.code}` : DEFAULT_LAYOUT;
}
/** Gates, gate walls, boundaries and obstacles for an airport (the shared plan unless it has its own gate row). */
export function gateLayout(airportId: string): GateLayout {
    return AIRPORTS.find(p => p.code === airportId)?.gateLayout ?? SHARED_GATES;
}

const expansions = (airportId: string) => gateLayout(airportId).EXPANSIONS ?? [];
/** Expansion areas still closed with this many gates bought, with the gate that will open each one. */
export function lockedExpansions(airportId: string, gates: number): (Expansion & { opensWith: number })[] {
    const areas = expansions(airportId), open = expansionsOpen(areas, gates);
    return areas.flatMap((x, i) => open[i] ? [] : [{ ...x, opensWith: expansionOpensWith(areas, i) }]);
}
/** Hoarding standing with this many gates bought: closed areas' walls and unbought gates' boarded doors. */
export function hoardingFor(airportId: string, gates: number): Rect[] { return standingHoarding(expansions(airportId), gates); }
/** Every hoarding piece the plan has, standing or not. */
export function allHoarding(airportId: string): Rect[] { return expansions(airportId).flatMap(x => [...x.hoarding, ...x.doors]); }
/** Static colliders with this many gates bought: the plan's own plus the hoarding still standing. */
export function obstaclesFor(airportId: string, gates: number): Rect[] {
    return [...gateLayout(airportId).OBSTACLES, ...hoardingFor(airportId, gates)];
}
/**
 * One navigation grid per floor plan and stage of its expansions, built once and shared by live play, L1 facts and
 * placement. Without a gate count every expansion counts as open.
 */
export function navFor(airportId: string, gates = Infinity): AirportNav {
    const plan = gatePlanKey(airportId);
    if (!plan) throw new Error(`No authored floor plan for airport ${airportId}.`);
    // Past the last expansion gate nothing more opens, so every larger count shares one grid.
    const last = Math.max(0, ...expansions(airportId).flatMap(x => x.gates.map(gate => gate + 1))), id = last ? `${plan}:gates:${Math.min(gates, last)}` : plan;
    let nav = navs.get(id);
    if (!nav) { nav = new AirportNav(CONFIG.width, CONFIG.height, CONFIG.cell, obstaclesFor(airportId, gates)); navs.set(id, nav); }
    return nav;
}
